import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { CategoryKey } from '@worldcoo/shared/core';
import { Searcher } from '@worldcoo/shared/searcher';
import type { AppRouter } from '@/lib/trpc/router.server';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { distinct, regionOptionsFor, type FilterOption } from './ngo-filter-options';

/** Inferred, since serialization makes the wire type differ from the domain one; type-only, so no server code ships. */
export type PartnerNgo = inferRouterOutputs<AppRouter>['portal']['getPartnerNgos']['ngos'][number];

export type PartnerNgoCampaign = inferRouterOutputs<AppRouter>['portal']['getPartnerNgoCampaigns']['campaigns'][number];

export type NgoFilters = {
	query: string;
	country: string | null;
	region: string | null;
	category: CategoryKey | null;
};

export const EMPTY_FILTERS: NgoFilters = { query: '', country: null, region: null, category: null };

/** Not `@worldcoo/monolith/forms/countries`, which is English-only. */
function useCountryNamer(): (code: string) => string {
	const { language } = useTranslation();
	return useMemo(() => {
		const names = new Intl.DisplayNames([language], { type: 'region' });
		return (code: string) => {
			try {
				return names.of(code.toUpperCase()) ?? code;
			} catch {
				return code;
			}
		};
	}, [language]);
}

/** Options come from the full roster, so a choice never empties another list; region is the exception. */
export function usePartnerNgos(filters: NgoFilters) {
	const { data } = useSuspenseQuery(trpc.portal.getPartnerNgos.queryOptions());
	const nameCountry = useCountryNamer();
	const ngos = data.ngos;

	const countryOptions = useMemo<FilterOption[]>(
		() =>
			distinct(ngos.map((ngo) => ngo.countryCode))
				.map((value) => ({ value, label: nameCountry(value) }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[ngos, nameCountry],
	);

	const regionOptions = useMemo(() => regionOptionsFor(ngos, filters.country), [ngos, filters.country]);

	const filtered = useMemo(() => {
		// Accent-insensitive per word: 95 of the 133 names carry a diacritic.
		const terms = Searcher.normalizeString(filters.query).split(/\s+/).filter(Boolean);
		return ngos.filter((ngo) => {
			const name = Searcher.normalizeString(ngo.name);
			if (!terms.every((term) => name.includes(term))) return false;
			if (filters.country && ngo.countryCode !== filters.country) return false;
			if (filters.region && ngo.region !== filters.region) return false;
			if (filters.category && !ngo.categories.includes(filters.category)) return false;
			return true;
		});
	}, [ngos, filters]);

	return { ngos, filtered, countryOptions, regionOptions };
}
